/* eslint-disable @typescript-eslint/no-unused-vars */
import { Fieldset, FormSection, Heading, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectSchema } from 'yup';
import { FormHelpers, OnFormInitialValuesChange } from '../../../screens/utils';
import { MixedComponentsProps } from '../../../types/mixedComponentProps';
import { NumericInputFormField } from '../../Fieldset/NumericInputFormField';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { Expense } from '../components/Expense';
import { getInitialValues } from './initialValues';
import { ExpenseDetailsFormModel_PL, ExpenseGroupType } from './formModel';
import { getValidationSchema } from './validationSchema';
import { sumNumbersArray } from '../../../services/common/miscUtils';

export const ExpenseDetailsForm_PL: React.FC<MixedComponentsProps> = ({ formHelpers }) => {
  const [initialValues] = useState(getInitialValues);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<ExpenseDetailsFormModel_PL>>();

  const { t } = useTranslation();

  useEffect(() => {
    setValidationSchema(getValidationSchema());
  }, []);

  const {
    formSectionProps,
    onFormSubmitSuccess,
    onFormInitialValuesChange,
    onStartEditInSummaryMode,
    summaryMode,
    isEditing,
    isDisabledFormSummary,
  } = formHelpers;

  const onSubmit = (values: ExpenseDetailsFormModel_PL) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };
  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(`expenseDetailsForm:title`), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Heading level={6} className="u-mt-none u-pt-none u-mb u-text-left">
        {t('expenseDetailsForm:subtitle')}
      </Heading>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnChange={false}
      >
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent
          onFormInitialValuesChange={onFormInitialValuesChange}
          disabled={isDisabledFormSummary}
          hideSubmitButton={summaryMode && !isEditing}
          isSummaryMode={summaryMode}
          formHelpers={formHelpers}
        />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = {
  onFormInitialValuesChange: OnFormInitialValuesChange;
  disabled: boolean;
  hideSubmitButton?: boolean;
  isSummaryMode?: boolean;
  formHelpers: FormHelpers;
};

const updateTotal = (
  setFieldValue: FormikHelpers<ExpenseDetailsFormModel_PL>['setFieldValue'],
  values: ExpenseDetailsFormModel_PL
) => {
  const isExpense = (value: any) => Array.isArray(value) && value[0].payment !== undefined;

  const paymentsArray = Object.values(values).reduce((payments, fieldValue) => {
    return [
      ...payments,
      ...(isExpense(fieldValue)
        ? (fieldValue as ExpenseGroupType).map(({ payment }) => (Number.isNaN(payment) ? 0 : payment))
        : [0]),
    ];
  }, [] as Array<number>);

  setFieldValue('total', sumNumbersArray(paymentsArray));
};

const FormContent: React.FC<FormContentProps> = ({
  onFormInitialValuesChange,
  disabled,
  hideSubmitButton,
  isSummaryMode,
  formHelpers,
}) => {
  const { initialValues, validateForm, submitForm, values, setFieldValue } =
    useFormikContext<ExpenseDetailsFormModel_PL>();
  const { onFormValuesChange } = formHelpers;

  useEffect(() => {
    // onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    updateTotal(setFieldValue, values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.personalLoan,
    values.carFinance,
    values.creditCards,
    values.otherDebt,
    values.otherCommitments,
    values.mortgage,
  ]);

  useEffect(() => {
    onFormValuesChange(values);
  }, [values, onFormValuesChange]);

  return (
    <>
      <Fieldset disabled={disabled}>
        <Expense expenseFieldName="personalLoan" />
        <Expense expenseFieldName="carFinance" />
        <Expense expenseFieldName="creditCards" />
        <Expense expenseFieldName="otherDebt" />
        <Expense expenseFieldName="otherCommitments" />
        <Expense expenseFieldName="mortgage" />

        <Layout className="u-pt">
          <Layout.Item default="1/4" s="1/1" />
          <Layout.Item default="3/4" s="1/1">
            <Fieldset.Row>
              <Layout>
                <Layout.Item default="2/3" s="1/1">
                  <Layout>
                    <Layout.Item default="3/4" s="1/1">
                      <NumericInputFormField
                        isDisabled
                        fieldName="total"
                        labelTranslationKey="expenseDetailsForm:total:label"
                        placeholderTranslationKey="expenseDetailsForm:total:placeholder"
                        type="money"
                      />
                    </Layout.Item>
                  </Layout>
                </Layout.Item>
              </Layout>
            </Fieldset.Row>
          </Layout.Item>
        </Layout>
      </Fieldset>

      {!hideSubmitButton && (
        <FormSectionSubmitButton disabled={disabled} onClick={submitForm} isSummaryMode={isSummaryMode} />
      )}
    </>
  );
};
